/**
 * Screen-list operations (Studio): explicit ordering for drag-and-drop and
 * whole-screen duplication. Pure — callers apply the returned patches/
 * elements through the existing mutate + captureUndo path.
 */
import { duplicateElements } from "@excalidraw/element";
import { getElementsOverlappingFrame } from "@excalidraw/element";
import { arrayToMap } from "@excalidraw/common";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

const GAP = 80; // scene units between a screen and its duplicate

/** customData patches renumbering every screen after a drag from → to. */
export const screenOrderPatches = (
  orderedFrames: readonly ExcalidrawFrameLikeElement[],
  fromIndex: number,
  toIndex: number,
): { id: string; customData: Record<string, unknown> }[] => {
  const next = [...orderedFrames];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(Math.max(0, Math.min(next.length, toIndex)), 0, moved);
  return next.map((frame, index) => ({
    id: frame.id,
    customData: { ...frame.customData, screenOrder: index },
  }));
};

/**
 * Deep-copies a screen: the frame plus every member element, fresh ids,
 * placed beside the original. Transition/camera/duration config rides along
 * in customData (the natural Smart-Animate authoring flow: duplicate, then
 * move things). Returns the new elements to append to the scene.
 */
export const duplicateScreen = (
  elements: readonly ExcalidrawElement[],
  frame: ExcalidrawFrameLikeElement,
): { newElements: ExcalidrawElement[]; newFrameId: string } => {
  const members = getElementsOverlappingFrame(
    elements,
    frame,
    arrayToMap(elements),
  ).filter((el) => el.id !== frame.id);
  const dx = frame.width + GAP;
  const { duplicatedElements } = duplicateElements({
    type: "everything",
    elements: [frame, ...members],
    overrides: ({ duplicateElement, origElement, origIdToDuplicateId }) => ({
      x: duplicateElement.x + 0, // keep — offset applied below uniformly
      ...(origElement.id === frame.id
        ? {}
        : { frameId: origIdToDuplicateId.get(frame.id) ?? null }),
    }),
  });
  const newElements = duplicatedElements.map(
    (el) => ({ ...el, x: el.x + dx } as ExcalidrawElement),
  );
  const newFrame = newElements.find(
    (el) => el.type === frame.type && el.id !== frame.id,
  );
  return {
    newElements,
    newFrameId: newFrame?.id ?? "",
  };
};
